// This file contains validation schemas using Zod for various fields in the application.
import { z, ZodTypeAny } from "zod"
import { isBefore, isValid, parseISO } from "date-fns"
import {
  BANK_ACCOUNT_NUMBER_REGEX,
  CITIZEN_SERVICE_NUMBER_REGEX,
  DUTCH_PHONE_NUMBER_REGEX,
  HOUSE_NUMBER_REGEX,
  KVK_NUMBER_REGEX,
  PHONE_NUMBER_REGEX,
  POSTAL_CODE_REGEX,
  VAT_NUMBER_REGEX,
  WEBSITE_REGEX,
} from "./regex"
import { MTTFunctionsEnum } from "../types/roles/mttRoles"

// nameField is a validation schema for names.
// It requires a minimum length of 3 characters.
export const nameField = z.string().min(3, { message: "Naam is verplicht" })

export const bsnField = z
  .string({
    message: "BSN is verplicht",
  })
  .refine((val) => val === "" || CITIZEN_SERVICE_NUMBER_REGEX.test(val), {
    message: "BSN moet exact 9 cijfers bevatten",
  })

// firstNameField is a validation schema for first names.
// It requires a minimum length of 2 characters.
export const firstNameField = z
  .string()
  .min(2, { message: "Voornaam is verplicht" })

// lastNameField is a validation schema for last names.
// It requires a minimum length of 2 characters.
export const lastNameField = z
  .string()
  .min(2, { message: "Achternaam is verplicht" })

export const namePrefixField = z.string().optional()

// emailField is a validation schema for email addresses.
// It requires a minimum length of 3 characters and checks if the input is a valid email format.
export const emailField = z
  .string()
  .min(3, { message: "E-mailadres is verplicht" })
  .email({ message: "E-mailadres is ongeldig" })

// emailFieldOptional is a validation schema for optional email addresses.
export const emailFieldOptional = z
  .string()
  .nullable()
  .refine((val) => !val || z.string().email().safeParse(val).success, {
    message: "E-mailadres is ongeldig",
  })

// postalCodeField is a validation schema for postal codes.
// It requires a minimum length of 6 characters and checks if the input matches the defined postal code regex.
export const postalCodeField = z
  .string()
  .min(6, { message: "Postcode is ongeldig" })
  .regex(POSTAL_CODE_REGEX, "Ongeldige postcode, gebruik het formaat 1234AB")

// houseNumberField is a validation schema for house numbers.
// It requires a minimum length of 1 character and checks if the input matches the defined house number regex.
export const houseNumberField = z
  .string()
  .min(1, { message: "Huisnummer is verplicht" })
  .regex(HOUSE_NUMBER_REGEX, "Huisnummer is ongeldig")

// streetField is a validation schema for street names.
// It requires a minimum length of 2 characters.
export const streetField = z.string().min(2, { message: "Straat is verplicht" })

// cityField is a validation schema for city names.
// It requires a minimum length of 2 characters.
export const cityField = z.string().min(2, { message: "Plaats is verplicht" })

// phoneNumberField is a validation schema for phone numbers.
export const phoneNumberField = z
  .string()
  .min(1, { message: "Telefoonnummer is verplicht" })
  .regex(PHONE_NUMBER_REGEX, { message: "Ongeldig telefoonnummer" })
  .refine(
    (val) => {
      // If it starts with +31, it must match the Dutch format
      if (val.startsWith("+31")) {
        return DUTCH_PHONE_NUMBER_REGEX.test(val)
      }

      return true
    },
    {
      message: "Ongeldig Nederlands telefoonnummer.",
    },
  )

// For strict Dutch-only validation (for example, organization forms)
export const dutchPhoneNumberField = z
  .string()
  .min(1, { message: "Telefoonnummer is verplicht" })
  .regex(DUTCH_PHONE_NUMBER_REGEX, {
    message: "Ongeldig Nederlands telefoonnummer.",
  })

// dutchPhoneNumberFieldOptional is the optional variant of dutchPhoneNumberField
export const dutchPhoneNumberFieldOptional = z
  .string()
  .nullable()
  .refine((val) => !val || DUTCH_PHONE_NUMBER_REGEX.test(val), {
    message: "Ongeldig Nederlands telefoonnummer.",
  })

// birthdateField is a validation schema for birthdates.
// It checks if the input can be parsed into a valid date.
export const birthdateField = z.string().refine(
  (val) => {
    const date = parseISO(val)

    return isValid(date) && isBefore(date, new Date())
  },
  {
    message: "Geldige geboortedatum is verplicht",
  },
)

// functionField is a validation schema for job functions.
// It requires at least one function to be selected.
export const functionField = z
  .array(MTTFunctionsEnum)
  .min(1, { message: "Minimaal één functie is verplicht" })

// kvkNumberField is a validation schema for KVK numbers.
// It checks if the input matches the defined KVK number regex.

// kvkNumberFieldOptional is a validation schema for optional KVK numbers.
export const kvkNumberFieldOptional = z
  .string()
  .nullable()
  .refine((val) => val === null || KVK_NUMBER_REGEX.test(val), {
    message: "KvK-nummer is ongeldig. Het moet uit 8 cijfers bestaan",
  })

// kvkNumberField is a validation schema for required KVK numbers.
export const kvkNumberField = z
  .string()
  .min(8, { message: "KvK-nummer moet uit 8 cijfers bestaan" })
  .regex(KVK_NUMBER_REGEX, {
    message: "KvK-nummer is ongeldig. Het moet uit 8 cijfers bestaan",
  })

// vatNumberFieldOptional is a validation schema for optional VAT numbers.
export const vatNumberFieldOptional = z
  .string()
  .nullable()
  .transform((v) => (v === null ? null : v.trim()))
  .refine((val) => val === null || val === "" || VAT_NUMBER_REGEX.test(val), {
    message: "Ongeldig Nederlands btw-nummerformaat (bijv. NL123456789B01)",
  })

// createDateRangeSchema is a function that creates a Zod schema for validating date ranges.
// It takes two keys for the start and end dates, and optional custom error messages.
// It ensures that both dates are valid ISO strings and that the start date is before the end date.
export function createDateRangeSchema<T extends string, U extends string>(
  startKey: T,
  endKey: U,
  messages?: {
    requiredStart?: string
    requiredEnd?: string
    invalidRange?: string
  },
) {
  return z
    .object({
      [startKey]: z.string().min(1, {
        message: messages?.requiredStart ?? "Startdatum is verplicht",
      }),
      [endKey]: z.string().min(1, {
        message: messages?.requiredEnd ?? "Einddatum is verplicht",
      }),
    } as unknown as Record<T | U, z.ZodType<string>>)
    .superRefine((data, ctx) => {
      const start = new Date(data[startKey])
      const end = new Date(data[endKey])

      if (start > end) {
        ctx.addIssue({
          path: [endKey],
          message:
            messages?.invalidRange ??
            "Einddatum mag niet vóór de startdatum liggen",
          code: z.ZodIssueCode.custom,
        })
      }
    })
}

export const requiredStringField = z
  .string({
    message: "Dit veld is verplicht",
  })
  .min(1, { message: "Dit veld is verplicht" })

export const requiredNumberField = z.number({
  message: "Dit veld is verplicht",
})

export const requiredPostiveNumberField = z
  .number({ invalid_type_error: "Dit veld is verplicht" })
  .min(1, { message: "Waarde moet groter zijn dan 0" })

export const nullableInput = <T extends ZodTypeAny>(
  schema: T,
  message = "Dit veld is verplicht",
) => {
  return schema.nullable().transform((val, ctx) => {
    if (val === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        fatal: true,
        message,
      })

      return z.NEVER
    }

    return val
  })
}

export const bankAccountNumberField = z
  .string()
  .regex(BANK_ACCOUNT_NUMBER_REGEX, {
    message:
      "Ongeldig bankrekeningnummer. Gebruik het IBAN-formaat (bijv. NL12ABCD1234567890)",
  })

export const websiteField = z
  .string()
  .nullable()
  .refine((val) => val === null || val === "" || WEBSITE_REGEX.test(val), {
    message: "Ongeldige website URL",
  })
